import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { theme, Tooltip } from 'antd';

import { useStores } from '@/hooks/use-stores';
import { PackageCareAbleOptions } from '@/constants/enums';

import {
  PackageCareIndicationContainer,
  PackageCareIndicationTooltipGlobalStyle,
} from './PackageCareIndication.styles';
import { Entity } from '../../NodeWidget.types';
import {
  formatEndDate,
  getCareProgramIcon,
  getFormattedSfsContract,
  shouldDisplayCareStatus,
} from './PackageCareIndication.utils';
import { isSelectedNodeOnMap } from '@/comp/MapBuilder/utils/utils';

const PackageCareIndication = observer(
  ({
    entity,
    mapView,
    className,
    backgroundColor,
    width,
    height,
  }: {
    entity: Entity;
    mapView?: boolean;
    className?: string;
    backgroundColor?: string;
    width?: number;
    height?: number;
  }) => {
    const { stationStore, mapBuilderStore } = useStores();
    const { t } = useTranslation();
    const { token } = theme.useToken();

    const currentAsset = stationStore.assetsDevices?.filter(device => device.deviceId === entity?.deviceId)[0];
    const isSelected = isSelectedNodeOnMap(entity.deviceId, mapBuilderStore.selectedDevice?.deviceId, true);
    const displayCareStatus = shouldDisplayCareStatus(currentAsset, entity, t);

    const formattedSfsContract = getFormattedSfsContract(currentAsset?.SfsContract);
    const CareProgramIcon = getCareProgramIcon(formattedSfsContract);
    const isNotCareAble = formattedSfsContract?.includes(PackageCareAbleOptions.NotCareAble);
    const isNoCareProgram = formattedSfsContract?.includes(PackageCareAbleOptions.NoCareProgram);

    if (mapView && (isNotCareAble || !displayCareStatus)) {
      return null;
    }

    return (
      <div className={className}>
        <PackageCareIndicationTooltipGlobalStyle />
        <Tooltip
          title={
            isNoCareProgram
              ? null
              : t('packageCareTooltip', {
                  careAgreementId: currentAsset?.careAgreementId,
                  endDate: currentAsset?.endDate ? formatEndDate(currentAsset?.endDate) : t('invalidDate'),
                })
          }
          placement={mapView ? 'right' : 'bottomLeft'}
          arrow={{ pointAtCenter: true }}
          classNames={{ root: 'package-care-tooltip' }}
        >
          {CareProgramIcon && (
            <PackageCareIndicationContainer
              $shouldNotDisplayCareStatus={isNotCareAble || !displayCareStatus}
              $mapView={mapView}
              $isNoCareProgram={isNoCareProgram}
              $isSelected={isSelected}
              $backgroundColor={backgroundColor}
            >
              <CareProgramIcon
                width={width ?? 85}
                height={height ?? 32}
                checkColor={isNoCareProgram ? token.colorIcon : null}
                backgroundColor={isNoCareProgram ? token.controlItemBgActiveDisabled : null}
              />
            </PackageCareIndicationContainer>
          )}
        </Tooltip>
      </div>
    );
  },
);

export default PackageCareIndication;
